import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { MobileHeaderCard } from '~/components/MobileHeaderCard';
import { useDateLang } from '~/hooks/useDateLang';
import { useMounted } from '~/hooks/useMounted';
import {
  findPickupPointForActiveAddress,
  usePickupZones,
} from '~/hooks/usePickupZones';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import {
  checkoutStore,
  deliveryMethodAvailabilityStore,
  orderStore,
  userStore,
} from '~/stores';
import { getAddressName } from '~/utils/addressName';
import capitalize from '~/utils/capitalize';

interface HeaderAddressProps {
  className?: string;
  shortTitle?: boolean;
  onClick?: () => void;
}

const HeaderAddress = ({
  className,
  shortTitle,
  onClick,
}: HeaderAddressProps) => {
  const { timeFormat } = useTimeFormat();
  const { locale } = useDateLang();
  const { t } = useTranslation();
  const mounted = useMounted();

  // Gate the eta-derived label behind mount: `etaCalculation` is a persisted
  // MobX store that can already be hydrated on the first client render even
  // though `mounted` is still false, so we must fall back to the same
  // "empty eta" default the server rendered to avoid a hydration mismatch.
  const { args, message, isFallback } = mounted
    ? orderStore.getDeliveryLabel({
        timeFormat,
        locale,
        onlySlot: true,
        onlyTime: shortTitle,
      })
    : orderStore.getDefaultDeliveryLabel();

  // Tab companies show the real ETA in the tabs, so the hard-coded "30 min"
  // default never renders here; other tenants keep it on purpose.
  const isPlaceholderLabel =
    company.showDeliveryMethodSwitcher && Boolean(isFallback);

  const isPickupAddress = userStore.deliveryAddress?.usage === 'pickup';
  // Only meaningful once mounted: `checkoutStore.deliveryMethod` is a
  // persisted MobX field that can already be hydrated before `mounted` flips,
  // same trap as the `etaCalculation` guard above.
  const isMethodMismatched =
    mounted &&
    company.showDeliveryMethodSwitcher &&
    checkoutStore.isPickupSelected !== isPickupAddress;

  // Mount-gated (SSR renders without an address) and hidden while the stored
  // address' usage doesn't match the active tab.
  const hasAddress =
    mounted && Boolean(userStore.deliveryAddress) && !isMethodMismatched;

  // Heimkaup's pickup points carry their own name ("Nettó Mjódd"); the address
  // flow has no location-name field, so without this the card would fall back
  // to the street of the warehouse behind the point. Scoped to the tenants
  // with the header tabs — `checkoutStore.isPickupSelected` alone is also true
  // for checkout-only method selection, where this card must not change.
  const showsPickupPointName =
    hasAddress &&
    company.showDeliveryMethodSwitcher &&
    checkoutStore.isPickupSelected &&
    isPickupAddress;

  const { pickupPoints, isLoading: isPickupZonesLoading } =
    usePickupZones(showsPickupPointName);

  const pickupPointName = showsPickupPointName
    ? findPickupPointForActiveAddress(userStore.deliveryAddress, pickupPoints)
        ?.name
    : undefined;

  const caption = (() => {
    if (company.useStaticDeliveryEta) {
      return t('phrases:staticDeliveryEta');
    }

    // Pickup's ETA is already shown in the tab beneath this card
    // (HeaderDeliverySwitcher) — repeating it here read as a duplicate, so
    // pickup always gets a fixed label instead of a time value. Gated the
    // same as isMethodMismatched: `checkoutStore.deliveryMethod` can be
    // CLICK_AND_COLLECT for companies with checkout-only method selection
    // (no header tabs), and this card must not switch to a pickup label for
    // them.
    if (
      mounted &&
      company.showDeliveryMethodSwitcher &&
      checkoutStore.isPickupSelected
    ) {
      return hasAddress && !shortTitle
        ? t('phrases:pickupAt')
        : t('checkout:address:tabPickup');
    }

    if (shortTitle && !isPlaceholderLabel) {
      return args.date || args.time;
    }

    // The slot/ETA above belongs to the stored address' own method — showing
    // it under the other tab's prompt is the same stale-address bug as the
    // title. The per-method zone probe is scoped to the address' location but
    // not its usage, so it stays valid to show under the prompt.
    if (isMethodMismatched) {
      return deliveryMethodAvailabilityStore.courierEtaRange
        ? t('phrases:inMinutes', {
            time: deliveryMethodAvailabilityStore.courierEtaRange,
          })
        : t('checkout:address:tabDelivery');
    }

    if (isPlaceholderLabel) {
      return t('checkout:address:tabDelivery');
    }

    return args.time === '30' ? t(message, args) : args.time;
  })();

  const title = (() => {
    if (shortTitle && !isPlaceholderLabel) {
      return args.time;
    }

    if (!mounted) {
      return t('selectDeliveryAddress');
    }

    if (!userStore.deliveryAddress || isMethodMismatched) {
      return company.showDeliveryMethodSwitcher &&
        checkoutStore.isPickupSelected
        ? t('selectPickupLocation')
        : t('selectDeliveryAddress');
    }

    if (pickupPointName) {
      return pickupPointName;
    }

    const { type, address1, shortAddress, city } = userStore.deliveryAddress;

    return getAddressName(
      { type, street: address1 || shortAddress, city },
      type ? capitalize(type) : shortAddress,
    );
  })();

  // The pickup prompt reuses the shop icon from the pickup-point map marker
  // (PickupMapMarker) instead of staying iconless, so it reads as "go pick a
  // pickup point" rather than as a plain unresolved state.
  const icon = hasAddress
    ? 'time'
    : isMethodMismatched && checkoutStore.isPickupSelected
      ? 'shop-building'
      : undefined;

  return (
    <MobileHeaderCard
      observerKey="header.deliveryAddress"
      icon={icon}
      caption={caption}
      title={title}
      isLoading={
        mounted &&
        (userStore.deliveryAddressMeta.isLoading ||
          orderStore.isETALoading ||
          (showsPickupPointName && isPickupZonesLoading))
      }
      onClick={onClick}
      className={className}
    />
  );
};

export default observer(HeaderAddress);
